import { useQuery } from "@tanstack/react-query";

import type { MembershipHosClocksResult } from "@storyboard/web-client";
import { getMembershipHosClocks, hosKeys } from "@storyboard/web-client";
import { transport } from "@/config";
import type { DriverHosSummary } from "@/types/driverHos";
import { summarizeDriverHos } from "./driverHosSummary";
import { useRefreshFailureEvent } from "./useRefreshFailureEvent";

/**
 * How often an open Context tab re-reads its driver's clocks. Platform Science reports a timer on duty-status changes
 * and clock milestones only, and the server counts running clocks down to each read, so every poll moves them.
 */
export const HOS_CLOCKS_REFRESH_MS = 60_000;

// Module scope so Query memoizes the select across renders; an inline arrow would re-summarize every render.
const selectMembershipHos = (data: MembershipHosClocksResult) => summarizeDriverHos(data.hos);

/**
 * One driver's hours of service, polled from the per-membership read for as long as the caller stays mounted.
 *
 * Its own poll rather than a field on the route read, so a slow or failing HOS read never holds up the route blocks.
 * `undefined` until the first answer lands; `null` once the read says the driver has no timer.
 */
export function useMembershipHosClocks(
  workspaceId: string | undefined,
  membershipId: string | undefined,
): DriverHosSummary | null | undefined {
  const query = useQuery({
    queryKey: hosKeys.membershipClocks(workspaceId ?? "", membershipId ?? ""),
    queryFn: () =>
      getMembershipHosClocks(transport, { workspaceId: workspaceId ?? "", membershipId: membershipId ?? "" }),
    enabled: Boolean(workspaceId) && Boolean(membershipId),
    refetchInterval: HOS_CLOCKS_REFRESH_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    select: selectMembershipHos,
  });
  useRefreshFailureEvent(query, "hos_clocks.membership_refresh_failed", "hosClocks", { workspaceId, membershipId });
  return query.data;
}
